import { asInstanceOf, isAbsent, isDefined, isNotNull, panic, UUID } from "@opendaw/lib-std";
import { Fields } from "./Fields";
import { Guard } from "./Guard";
export class Facade {
    #context;
    #box;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
    }
    get uuid() { return UUID.toString(this.#box.address.uuid); }
    get box() {
        if (!this.#box.isAttached()) {
            return panic(`${this.constructor.name} (${this.uuid}) has been removed`);
        }
        return this.#box;
    }
    get context() { return this.#context; }
    get attached() { return this.#box.isAttached(); }
    remove() { this.#context.edit(() => this.box.delete()); }
    bind(spec) { Fields.bind(this.#context, this, spec); }
    toString() { return `${this.constructor.name}(${this.uuid})`; }
}
export var Props;
(function (Props) {
    Props.apply = (target, props, name = "props") => {
        if (isAbsent(props)) {
            return target;
        }
        if (typeof props !== "object") {
            return panic(new TypeError(`${name}: expected an object, got ${Guard.describe(props)}`));
        }
        Object.entries(props).forEach(([key, value]) => {
            if (isAbsent(value)) {
                return;
            }
            const descriptor = findDescriptor(target, key);
            if (!isDefined(descriptor)) {
                return panic(new TypeError(`${name}.${key}: unknown property`));
            }
            if (isDefined(descriptor.set)) {
                target[key] = value;
                return;
            }
            const current = target[key];
            if (typeof current === "object" && isNotNull(current) && typeof value === "object" && isNotNull(value)) {
                if (Array.isArray(current)) {
                    if (!Array.isArray(value)) {
                        return panic(new TypeError(`${name}.${key}: expected an array`));
                    }
                    value.forEach((element, index) => {
                        if (index >= current.length) {
                            return panic(new RangeError(`${name}.${key}: index ${index} out of range`));
                        }
                        if (typeof current[index] === "object") {
                            Props.apply(current[index], element, `${name}.${key}.${index}`);
                        }
                        else {
                            current[index] = element;
                        }
                    });
                }
                else {
                    Props.apply(current, value, `${name}.${key}`);
                }
                return;
            }
            return panic(new TypeError(`${name}.${key}: property is read-only`));
        });
        return target;
    };
    Props.without = (props, ...keys) => {
        if (isAbsent(props)) {
            return {};
        }
        if (typeof props !== "object") {
            return panic(new TypeError(`props: expected an object, got ${Guard.describe(props)}`));
        }
        return Object.fromEntries(Object.entries(props).filter(([key]) => !keys.includes(key)));
    };
    const findDescriptor = (target, key) => {
        let current = target;
        while (isNotNull(current) && current !== Object.prototype) {
            const descriptor = Object.getOwnPropertyDescriptor(current, key);
            if (isDefined(descriptor)) {
                return descriptor;
            }
            current = Object.getPrototypeOf(current);
        }
        return undefined;
    };
})(Props || (Props = {}));
export var Accessors;
(function (Accessors) {
    Accessors.pointerBox = (pointer, type) => pointer.targetVertex.mapOr(vertex => asInstanceOf(vertex.box, type), null);
})(Accessors || (Accessors = {}));
export var Parameters;
(function (Parameters) {
    Parameters.resolve = (target, path) => Fields.resolve(target, path)
        .unwrapOrElse(() => panic(new RangeError(`'${path}' is not a parameter of ${describe(target)}. ` +
        `Available: ${Fields.paths(target).join(", ")}`)));
    Parameters.pathOf = (target, field) => Fields.paths(target).find(path => Fields.resolve(target, path).contains(field)) ?? null;
    const describe = (target) => target.constructor.name;
})(Parameters || (Parameters = {}));
